/**
 * Doctors' health articles for ClinVea Care: the author's own list (doctor) and ClinVea's review queue
 * (super admin). Published articles are read by the public site, not the app.
 */
import { fetchApi } from '../client';
import type { PagedResponse } from '../../../types/pagination';

export type ArticleStatus = 'DRAFT' | 'SUBMITTED' | 'PUBLISHED' | 'CHANGES_REQUESTED' | 'UNPUBLISHED';

export interface ArticleDraft {
  title: string;
  summary: string;
  body: string;
  specialty?: string | null;
}

export interface MyArticle extends ArticleDraft {
  id: string;
  status: ArticleStatus;
  /** Set once first published; the public address stays the same after edits. */
  slug?: string | null;
  reviewNote?: string | null;
  submittedAt?: string | null;
  publishedAt?: string | null;
  updatedAt: string;
}

export interface ArticleForReview extends ArticleDraft {
  id: string;
  /** Built by the server from the plain-text body, with all author text escaped. */
  bodyHtml: string;
  authorName?: string | null;
  authorSlug?: string | null;
  /** What the automatic screen noticed (contact details, links, prices, claims). */
  flags: string[];
  /** An edit of an article that was already published. */
  republish: boolean;
  submittedAt?: string | null;
}

/** A published article as the super admin sees it, to retract it (GET /api/admin/articles/published). */
export interface PublishedArticle extends ArticleDraft {
  id: string;
  authorName?: string | null;
  authorSlug?: string | null;
  publishedAt?: string | null;
}

/** The retract note the server accepts: 3 to 1000 characters after trimming. */
export const RETRACT_NOTE_MIN = 3;
export const RETRACT_NOTE_MAX = 1000;
export const isValidRetractNote = (note: string) => {
  const n = note.trim().length;
  return n >= RETRACT_NOTE_MIN && n <= RETRACT_NOTE_MAX;
};

/** The My Articles tabs; the server maps each to its statuses (medopd-docs 117). */
export type ArticleTab = 'all' | 'drafts' | 'review' | 'published';

const json = (method: string, body?: unknown) => ({ method, body: body === undefined ? undefined : JSON.stringify(body) });
const DOCTOR = '/api/doctor/articles';
const ADMIN = '/api/admin/articles';
const id = (v: string) => encodeURIComponent(v);

export const doctorArticlesModule = {
  mine: () => fetchApi<MyArticle[]>(DOCTOR),
  /** One page of a tab, most recently updated first. */
  minePage: (tab: ArticleTab, page: number, size: number) =>
    fetchApi<PagedResponse<MyArticle>>(`${DOCTOR}?tab=${tab}&page=${page}&size=${size}`),
  /** Article count per status (every status present). */
  counts: () => fetchApi<Record<ArticleStatus, number>>(`${DOCTOR}/counts`),
  create: (d: ArticleDraft) => fetchApi<MyArticle>(DOCTOR, json('POST', d)),
  update: (articleId: string, d: ArticleDraft) => fetchApi<MyArticle>(`${DOCTOR}/${id(articleId)}`, json('PUT', d)),
  submit: (articleId: string) => fetchApi<MyArticle>(`${DOCTOR}/${id(articleId)}/submit`, json('POST')),
  withdraw: (articleId: string) => fetchApi<MyArticle>(`${DOCTOR}/${id(articleId)}/withdraw`, json('POST')),
  remove: (articleId: string) => fetchApi<void>(`${DOCTOR}/${id(articleId)}`, { method: 'DELETE' }),
  review: {
    queue: () => fetchApi<ArticleForReview[]>(`${ADMIN}/review`),
    /** One page of the queue, oldest submission first — reaches every submitted article. */
    queuePage: (page: number, size: number) =>
      fetchApi<PagedResponse<ArticleForReview>>(`${ADMIN}/review?page=${page}&size=${size}`),
    /** @param overrideReason needed when the automatic check asked for one (medopd-docs 146) */
    approve: (articleId: string, overrideReason?: string) => fetchApi<{ status: string }>(`${ADMIN}/${id(articleId)}/approve`,
      json('POST', overrideReason ? { overrideReason } : undefined)),
    requestChanges: (articleId: string, note: string) =>
      fetchApi<{ status: string }>(`${ADMIN}/${id(articleId)}/request-changes`, json('POST', { note })),
    /** One page of published articles, newest first (to retract one). */
    publishedPage: (page: number, size: number) =>
      fetchApi<PagedResponse<PublishedArticle>>(`${ADMIN}/published?page=${page}&size=${size}`),
    /** Takes a published article off ClinVea Care; the note (3–1000 characters) is required and kept on record. */
    retract: (articleId: string, note: string) =>
      fetchApi<{ status: string }>(`${ADMIN}/${id(articleId)}/retract`, json('POST', { note })),
  },
};
